/**
 * The store's logo as an EMBEDDED image for the customer papers (owner,
 * 2026-10-08: "where the logo").
 *
 * The print computer draws a slip by putting its HTML inside an SVG image and
 * painting that onto a canvas (raster.ts). An SVG used as an image loads
 * nothing from the network, so a logo `src` pointing at the media host would
 * print as an empty box. It goes in as a data URL instead, made once per logo
 * URL: loaded with CORS (the media host allows it — the QR sticker draws the
 * same logo onto a canvas), flattened onto white, capped in size so a large
 * upload does not bloat every job.
 *
 * Any failure is "no logo", never an error: a slip without its logo is still
 * a correct slip, and the head falls back to the centred name.
 */
const MAX_SIDE_PX = 360;
const LOAD_TIMEOUT_MS = 8_000;

const cache = new Map<string, Promise<string | null>>();

/**
 * Fetched, not loaded as an `<img crossorigin>`: the sidebar has usually shown
 * the same logo already, as a plain `<img>`, and the browser can answer a
 * CORS request from that cached non-CORS copy — which carries no
 * `Access-Control-Allow-Origin` (the media host only sends it to a request with
 * an `Origin`). The canvas is then tainted, `toDataURL` throws, and the logo
 * silently vanishes from the printed slip (seen on production, 2026-10-11).
 * `cache: "no-store"` makes a fresh CORS request; drawing from a blob URL is
 * same-origin, so nothing is tainted.
 */
async function load(url: string): Promise<HTMLImageElement> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LOAD_TIMEOUT_MS);
  try {
    const response = await fetch(url, { mode: "cors", cache: "no-store", signal: controller.signal });
    if (!response.ok) throw new Error(`logo-http-${response.status}`);
    const objectUrl = URL.createObjectURL(await response.blob());
    try {
      const image = new Image();
      image.src = objectUrl;
      await image.decode();
      return image;
    } finally {
      // The pixels are drawn before this resolves' caller returns; the bitmap
      // stays decoded on the element, so the blob URL can go.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    }
  } finally {
    clearTimeout(timer);
  }
}

async function build(url: string): Promise<string | null> {
  try {
    const image = await load(url);
    if (image.naturalWidth < 1 || image.naturalHeight < 1) return null;
    const scale = Math.min(1, MAX_SIDE_PX / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(image.naturalWidth * scale);
    canvas.height = Math.round(image.naturalHeight * scale);
    const context = canvas.getContext("2d");
    if (!context) return null;
    // A transparent logo on thermal paper: transparent reads as paper.
    context.fillStyle = "#fff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
}

export function paperLogoDataUrl(url: string | null): Promise<string | null> {
  if (!url) return Promise.resolve(null);
  if (url.startsWith("data:")) return Promise.resolve(url);
  let entry = cache.get(url);
  if (!entry) {
    entry = build(url);
    cache.set(url, entry);
    // A failed load may be a passing network fault: let the next job retry.
    void entry.then((result) => {
      if (result === null) cache.delete(url);
    });
  }
  return entry;
}
